import React, { useState, useEffect, useRef } from 'react';
import { Search, MapPin, Building, ChevronDown, ArrowRight, X, Check, Phone, User, MessageSquare, Home, Mail, Calendar, Clock } from 'lucide-react';

import MainUrl from '../../MainUrl';

const QuoteModal = ({ isOpen, onClose, selectedLocationId = 0 }) => {
    const [name, setName] = useState('');
    const [mobile, setMobile] = useState('');
    const [mobileError, setMobileError] = useState('');
    // const [email, setEmail] = useState('');
    const [address, setAddress] = useState('');
    const [remarks, setRemarks] = useState('');
    const [selectedServices, setSelectedServices] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    // const [pickupDate, setPickupDate] = useState('');
    // const [pickupTime, setPickupTime] = useState('1');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [services, setServices] = useState([]);
    const [loadingServices, setLoadingServices] = useState(false);

    // Ref for dropdown to detect outside clicks
    const dropdownRef = useRef(null);

    // Format today's date as YYYY-MM-DD for min date attribute
    const today = new Date().toISOString().split('T')[0];

    // Fetch services list when component mounts or when isOpen changes
    useEffect(() => {
        if (isOpen) {
            fetchServices();
        }
    }, [isOpen]);

    // Function to fetch services/categories
    const fetchServices = async () => {
        try {
            setLoadingServices(true);
            const response = await fetch(`${MainUrl}web/getCategoryList`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            const data = await response.json();
            if (data.success) {
                // Filter out "All Services" option for the multi-select dropdown
                const servicesList = data.data.filter(service => service.category_name !== 'All Services');
                setServices(servicesList);
            } else {
                setError('Failed to fetch services');
            }
        } catch (err) {
            setError('Error fetching services: ' + err.message);
            console.error('Error fetching services:', err);
        } finally {
            setLoadingServices(false);
        }
    };

    // Effect to handle clicks outside the dropdown
    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        }

        // Attach the event listener
        if (showDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        // Clean up the event listener
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showDropdown]);

    // Reset form data
    const resetForm = () => {
        setName('');
        setMobile('');
        // setEmail('');
        setAddress('');
        setRemarks('');
        setSelectedServices([]);
        // setPickupDate('');
        // setPickupTime('1');
        setError('');
        setSuccess('');
    };

    // Reset form when modal is closed
    useEffect(() => {
        if (!isOpen) {
            resetForm();
        }
    }, [isOpen]);

    // Function to validate mobile number
    const validateMobile = (value) => {
        const mobileRegex = /^[0-9]{10}$/;
        if (!mobileRegex.test(value)) {
            setMobileError('Please enter a valid 10-digit mobile number');
            return false;
        }
        setMobileError('');
        return true;
    };

    // Handle mobile input change with validation
    const handleMobileChange = (e) => {
        const value = e.target.value;

        // Allow only numbers
        if (value === '' || /^[0-9]*$/.test(value)) {
            setMobile(value);
            if (value.length === 10) {
                validateMobile(value);
            } else if (mobileError && value.length < 10) {
                setMobileError('');
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate mobile before submission
        if (!validateMobile(mobile)) {
            return;
        }

        // Validate that at least one service is selected
        if (selectedServices.length === 0) {
            setError('Please select at least one service');
            return;
        }

        setIsSubmitting(true);
        setError('');
        setSuccess('');

        try {
            // Prepare the order details array based on selected services
            const orderDetails = selectedServices.map(serviceName => {
                // Find the service object by name
                const service = services.find(s => s.category_name === serviceName);

                return {
                    category_id: service ? service.category_id : 0,
                    sub_category_id: 0,
                    status: 1,
                    remarks: remarks
                };
            });

            const now = new Date();
            const year = now.getFullYear();
            const month = String(now.getMonth() + 1).padStart(2, '0');
            const day = String(now.getDate()).padStart(2, '0');
            const currentDate = `${year}-${month}-${day}`;

            // Prepare payload
            const payload = {
                apartment_id: selectedLocationId,
                laundry_id: 0,
                name: name,
                ph_no: mobile,
                email: '',
                address: address,
                pickup_date: currentDate,
                pickup_time: '',
                delivery_type: "1",
                created_by: 22,
                created_from: "Web",
                orderDetails: orderDetails
            };

            // Send data to API
            const response = await fetch(`${MainUrl}web/getOrderCreate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            const data = await response.json();

            if (data.success) {
                setSuccess('Your order has been placed successfully!');

                // Reset form after successful submission
                setTimeout(() => {
                    resetForm();
                    onClose();
                }, 2000);
            } else {
                setError(data.message || 'Failed to place order. Please try again.');
            }
        } catch (err) {
            setError('Error submitting form: ' + err.message);
            console.error('Error submitting form:', err);
        } finally {
            setIsSubmitting(false);
        }
    };

    const toggleService = (service) => {
        setShowDropdown(true); // Keep dropdown open when selecting services
        if (selectedServices.includes(service)) {
            setSelectedServices(selectedServices.filter(s => s !== service));
        } else {
            setSelectedServices([...selectedServices, service]);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl w-full max-w-xl relative overflow-hidden">
                {/* Modal Header */}
                <div className="bg-blue-700 text-white p-4">
                    <div className="flex justify-between items-center">
                        <h3 className="text-xl font-bold">Schedule Free Pickup Now!</h3>
                        <button
                            onClick={onClose}
                            className="text-white hover:text-gray-200"
                        >
                            <X size={24} />
                        </button>
                    </div>
                </div>

                {/* Modal Body */}
                <form onSubmit={handleSubmit} className="p-4">
                    {/* Two Column Layout */}
                    {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4"> */}
                    {/* Left Column */}
                    <div className="space-y-2">
                        {/* Name Input */}
                        <div>
                            <label className="block text-gray-700 mb-1 font-medium">Your Name</label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <User size={18} className="text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    className="block w-full pl-10 pr-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Enter your full name"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    required
                                />
                            </div>
                        </div>

                        {/* Mobile Input */}
                        <div>
                            <label className="block text-gray-700 mb-1 font-medium">Mobile Number</label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Phone size={18} className="text-gray-400" />
                                </div>
                                <input
                                    type="tel"
                                    className={`block w-full pl-10 pr-3 py-2 rounded-lg border ${mobileError ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'}`}
                                    placeholder="Enter 10-digit mobile number"
                                    value={mobile}
                                    onChange={handleMobileChange}
                                    maxLength={10}
                                    required
                                />
                            </div>
                            {mobileError && (
                                <p className="text-red-500 text-xs mt-1">{mobileError}</p>
                            )}
                        </div>

                        {/* Email Input */}
                        {/* <div>
                                <label className="block text-gray-700 mb-1 font-medium">Email Address</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Mail size={18} className="text-gray-400" />
                                    </div>
                                    <input
                                        type="email"
                                        className="block w-full pl-10 pr-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="Enter your email address"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                    />
                                </div>
                            </div> */}

                        {/* Address Input */}
                        <div>
                            <label className="block text-gray-700 mb-1 font-medium">Address</label>
                            <div className="relative">
                                <div className="absolute top-3 left-3 pointer-events-none">
                                    <Home size={18} className="text-gray-400" />
                                </div>
                                <textarea
                                    className="block w-full pl-10 pr-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Enter your complete address"
                                    value={address}
                                    onChange={(e) => setAddress(e.target.value)}
                                    rows="2"
                                    required
                                />
                            </div>
                        </div>

                        {/* Service Selection Dropdown */}
                        <div ref={dropdownRef}>
                            <label className="block text-gray-700 mb-1 font-medium">Services Required</label>
                            <div className="relative">
                                <div
                                    className={`flex items-center justify-between w-full px-3 py-2 rounded-lg border ${error && selectedServices.length === 0 ? 'border-red-500' : 'border-gray-300'} cursor-pointer`}
                                    onClick={() => setShowDropdown(!showDropdown)}
                                >
                                    <div className="flex flex-wrap gap-1">
                                        {selectedServices.length > 0 ? (
                                            selectedServices.map((service, index) => (
                                                <span key={index} className="bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded-full">
                                                    {service}
                                                </span>
                                            ))
                                        ) : (
                                            <span className="text-gray-500">Select services</span>
                                        )}
                                    </div>
                                    <ChevronDown size={18} className="text-gray-400 ml-2 flex-shrink-0" />
                                </div>

                                {showDropdown && (
                                    <div className="absolute z-10 mt-1 w-full bg-white rounded-lg border border-gray-200 shadow-lg max-h-60 overflow-y-auto">
                                        {loadingServices ? (
                                            <div className="flex justify-center items-center py-4">
                                                <div className="h-5 w-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                                                <span className="ml-2 text-gray-600">Loading services...</span>
                                            </div>
                                        ) : services.length > 0 ? (
                                            services.map((service, index) => (
                                                <div
                                                    key={index}
                                                    className="flex items-center px-3 py-2 hover:bg-blue-50 cursor-pointer"
                                                    onClick={(e) => {
                                                        e.stopPropagation(); // Prevent dropdown from closing when selecting service
                                                        toggleService(service.category_name);
                                                    }}
                                                >
                                                    <div className={`w-5 h-5 mr-2 flex-shrink-0 rounded border ${selectedServices.includes(service.category_name)
                                                        ? 'bg-blue-600 border-blue-600 flex items-center justify-center'
                                                        : 'border-gray-300'
                                                        }`}>
                                                        {selectedServices.includes(service.category_name) && (
                                                            <Check size={14} className="text-white" />
                                                        )}
                                                    </div>
                                                    <span>{service.category_name}</span>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="px-3 py-2 text-gray-500">No services available</div>
                                        )}
                                    </div>
                                )}
                            </div>
                            {error && selectedServices.length === 0 && (
                                <p className="text-red-500 text-xs mt-1">Please select at least one service</p>
                            )}
                        </div>

                        {/* Remarks Input */}
                        <div>
                            <label className="block text-gray-700 mb-1 font-medium">Remarks (Optional)</label>
                            <div className="relative">
                                <div className="absolute top-3 left-3 pointer-events-none">
                                    <MessageSquare size={18} className="text-gray-400" />
                                </div>
                                <textarea
                                    className="block w-full pl-10 pr-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Any specific requirements or questions?"
                                    value={remarks}
                                    onChange={(e) => setRemarks(e.target.value)}
                                    rows="3"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Right Column */}
                    <div className="space-y-4">
                        {/* Pickup Date */}
                        {/* <div>
                                <label className="block text-gray-700 mb-1 font-medium">Pickup Date</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Calendar size={18} className="text-gray-400" />
                                    </div>
                                    <input
                                        type="date"
                                        className="block w-full pl-10 pr-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        min={today}
                                        value={pickupDate}
                                        onChange={(e) => setPickupDate(e.target.value)}
                                        required
                                    />
                                </div>
                            </div> */}

                        {/* Pickup Time */}
                        {/* <div>
                                <label className="block text-gray-700 mb-1 font-medium">Preferred Pickup Time</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Clock size={18} className="text-gray-400" />
                                    </div>
                                    <select
                                        className="block w-full pl-10 pr-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        value={pickupTime}
                                        onChange={(e) => setPickupTime(e.target.value)}
                                        required
                                    >
                                        <option value="1">Any</option>
                                        <option value="2">Morning (8 AM - 12 PM)</option>
                                        <option value="3">Afternoon (12 PM - 4 PM)</option>
                                        <option value="4">Evening (4 PM - 8 PM)</option>
                                    </select>
                                </div>
                            </div> */}


                    </div>

                    {/* Full Width Elements */}
                    <div className="md:col-span-2">

                        {/* Error and Success Messages */}
                        {error && error !== 'Please select at least one service' && error !== 'Please accept the Terms and Conditions' && (
                            <div className="bg-red-100 text-red-700 p-3 rounded-lg text-sm mt-4">
                                {error}
                            </div>
                        )}

                        {success && (
                            <div className="bg-green-100 text-green-700 p-3 rounded-lg text-sm mt-4">
                                {success}
                            </div>
                        )}

                        {/* Terms and Conditions Checkbox */}
                        <div className="mt-4">
                            <div className="flex items-start">
                                {/* <div className="flex items-center h-5">
                                        <input
                                            id="terms"
                                            type="checkbox"
                                            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                            checked={true}
                                        />
                                    </div> */}
                                <div className="ml-3 text-sm">
                                    <label htmlFor="terms" className="text-gray-700">
                                        By clicking submit you agree to our{" "}
                                        <a
                                            href="/terms"
                                            target="_blank"
                                            className="text-blue-600 hover:text-blue-800 underline"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                // You can replace this with the actual terms page URL
                                                window.open('/terms', '_blank');
                                            }}
                                        >
                                            Terms and Conditions
                                        </a>
                                    </label>
                                </div>
                            </div>
                        </div>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            className="w-full mt-6 px-4 py-2 bg-orange-500 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? (
                                <>
                                    <div className="h-5 w-5 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    Processing...
                                </>
                            ) : (
                                'Submit Request'
                            )}
                        </button>
                    </div>
                    {/* </div> */}
                </form>
            </div>
        </div>
    );
};

export default QuoteModal;